import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, Eye, Users, Plus, Send } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import InstructorQuestionManager from "@/components/instructor/InstructorQuestionManager";
import PrintSettingsDialog from "@/components/instructor/PrintSettingsDialog";

interface InstructorTest {
  id: string;
  title: string;
  description?: string;
  subject: string;
  grade: string;
  topic: string;
  time_limit?: number;
  total_questions: number;
  is_published: boolean;
  created_at: string;
}

interface StudentList {
  id: string;
  list_name: string;
  instructor_student_emails: Array<{
    id: string;
    email: string;
    student_name: string;
  }>;
}

export default function InstructorTestEdit() {
  const { testId } = useParams<{ testId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [test, setTest] = useState<InstructorTest | null>(null);
  const [studentLists, setStudentLists] = useState<StudentList[]>([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    grade: "",
    topic: "",
    timeLimit: "60",
  });

  useEffect(() => {
    if (testId) {
      loadTest();
      loadStudentLists();
      loadSubjects();
    }
  }, [testId]);

  const loadSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('id, name');

      if (error) throw error;
      setSubjects(data || []);
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };

  const loadTest = async () => {
    if (!testId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('instructor_tests')
        .select('*')
        .eq('id', testId)
        .eq('instructor_id', user?.id)
        .single();

      if (error) throw error;

      // Check if subject is a GUID and resolve to name
      let subjectName = data.subject;
      if (data.subject && data.subject.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        const subjectRecord = subjects.find(s => s.id === data.subject);
        if (subjectRecord) {
          subjectName = subjectRecord.name;
        }
      }

      setTest({ ...data, subject: subjectName });
      setFormData({
        title: data.title,
        description: data.description || "",
        subject: subjectName,
        grade: data.grade,
        topic: data.topic,
        timeLimit: data.time_limit?.toString() || "60",
      });
    } catch (error) {
      console.error('Error loading test:', error);
      toast.error('Failed to load test');
      navigate('/instructor/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentLists = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('instructor_student_lists')
        .select(`
          id,
          list_name,
          instructor_student_emails (
            id,
            email,
            student_name
          )
        `)
        .eq('instructor_id', user.id);

      if (error) throw error;
      setStudentLists(data || []);
    } catch (error) {
      console.error('Error loading student lists:', error);
    }
  };

  const handleSendInvitations = async () => {
    if (!test || selectedLists.length === 0) {
      toast.error('Please select at least one student list');
      return;
    }

    const selectedStudentLists = studentLists.filter(list => selectedLists.includes(list.id));
    const totalStudents = selectedStudentLists.reduce((acc, list) => acc + list.instructor_student_emails.length, 0);

    if (totalStudents === 0) {
      toast.error('No students found in selected lists');
      return;
    }

    try {
      // First, get existing invitations for this test to avoid duplicates
      const { data: existingInvitations, error: existingError } = await supabase
        .from('test_invitations')
        .select('email')
        .eq('test_id', testId);

      if (existingError) {
        console.error('Error fetching existing invitations:', existingError);
      }

      const existingEmails = new Set(existingInvitations?.map(inv => inv.email) || []);
      console.log('Existing invitation emails:', existingEmails);

      // Collect emails for new invitations
      const newEmails = [];
      const skippedEmails = [];
      
      for (const list of selectedStudentLists) {
        for (const student of list.instructor_student_emails) {
          if (existingEmails.has(student.email)) {
            skippedEmails.push(student.email);
            console.log('Skipping duplicate invitation for:', student.email);
          } else {
            newEmails.push(student.email);
          }
        }
      }

      console.log('New emails to invite:', newEmails);
      console.log('Skipped emails (already invited):', skippedEmails);

      if (newEmails.length === 0) {
        if (skippedEmails.length > 0) {
          toast.error(`All selected students (${skippedEmails.length}) have already been invited to this test.`);
        } else {
          toast.error('No new invitations to send.');
        }
        return;
      }

      // Send invitations via edge function
      const { data, error } = await supabase.functions.invoke('send-test-invitation', {
        body: {
          testId: testId,
          emails: newEmails,
          instructorId: user?.id
        }
      });

      if (error) throw error;

      const { sent, failed, results } = data;
      
      let successMessage = `Invitations sent to ${sent} students!`;
      if (failed > 0) {
        successMessage += ` (${failed} failed to send)`;
      }
      if (skippedEmails.length > 0) {
        successMessage += ` (${skippedEmails.length} students were already invited)`;
      }

      // Show detailed results
      const registeredCount = results.filter(r => r.success && r.registered).length;
      const unregisteredCount = results.filter(r => r.success && !r.registered).length;
      
      if (registeredCount > 0 && unregisteredCount > 0) {
        successMessage += ` - ${registeredCount} registered users, ${unregisteredCount} will need to sign up first.`;
      } else if (unregisteredCount > 0) {
        successMessage += ` - All recipients will need to sign up first.`;
      }

      toast.success(successMessage);
      setShowInviteDialog(false);
      setSelectedLists([]);
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error(`Failed to send invitations: ${error.message}`);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveTest = async () => {
    if (!testId || !user) return;

    if (!formData.title.trim() || !formData.topic.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate content for inappropriate words
    const { validateContent } = await import('@/utils/contentValidation');
    
    const titleValidation = validateContent(formData.title);
    if (!titleValidation.isValid) {
      toast.error(`Title: ${titleValidation.message}`);
      return;
    }

    const topicValidation = validateContent(formData.topic);
    if (!topicValidation.isValid) {
      toast.error(`Topic: ${topicValidation.message}`);
      return;
    }

    if (formData.description.trim()) {
      const descriptionValidation = validateContent(formData.description);
      if (!descriptionValidation.isValid) {
        toast.error(`Description: ${descriptionValidation.message}`);
        return;
      }
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('instructor_tests')
        .update({
          title: formData.title.trim(),
          description: formData.description.trim(),
          topic: formData.topic.trim(),
          time_limit: parseInt(formData.timeLimit) || null,
        })
        .eq('id', testId)
        .eq('instructor_id', user.id);

      if (error) throw error;

      toast.success('Test updated successfully!');
      loadTest(); // Reload test data
    } catch (error) {
      console.error('Error updating test:', error);
      toast.error('Failed to update test');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishTest = async () => {
    if (!testId || !user || !test) return;

    if (test.total_questions === 0) {
      toast.error('Please add questions before publishing the test');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('instructor_tests')
        .update({ is_published: !test.is_published })
        .eq('id', testId)
        .eq('instructor_id', user.id);

      if (error) throw error;

      toast.success(test.is_published ? 'Test unpublished' : 'Test published successfully!');
      loadTest();
    } catch (error) {
      console.error('Error publishing test:', error);
      toast.error('Failed to update test status');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="space-y-6">
            <Skeleton className="h-8 w-64" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Skeleton className="h-96 w-full" />
              </div>
              <div>
                <Skeleton className="h-64 w-full" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Test Not Found</h1>
            <Button asChild>
              <Link to="/instructor/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Edit Test: {test.title}
            </h1>
            <div className="flex items-center gap-4">
              <Badge variant={test.is_published ? "default" : "secondary"}>
                {test.is_published ? "Published" : "Draft"}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Grade {test.grade} • {test.total_questions} questions
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <PrintSettingsDialog 
              testId={testId!} 
              testData={{
                title: test.title,
                grade: test.grade,
                subject: test.subject,
                topic: test.topic,
                time_limit: test.time_limit,
                total_questions: test.total_questions
              }}
            />
            <Button
              variant="outline"
              onClick={handlePublishTest}
              disabled={saving}
            >
              <Eye className="h-4 w-4 mr-2" />
              {test.is_published ? "Unpublish" : "Publish"}
            </Button>
            {test.is_published && (
              <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Users className="h-4 w-4 mr-2" />
                    Invite Students
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Invite Students to Test</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Select which student lists to invite to "{test.title}"
                    </p>
                    
                    {studentLists.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-sm text-muted-foreground mb-3">
                          No student lists found. Create a student list first.
                        </p>
                        <Button variant="outline" size="sm" asChild>
                          <Link to="/instructor/students/create-list">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Student List
                          </Link>
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Invites go to everyone in selected lists.</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (selectedLists.length === studentLists.length) {
                                setSelectedLists([]);
                              } else {
                                setSelectedLists(studentLists.map(l => l.id));
                              }
                            }}
                          >
                            {selectedLists.length === studentLists.length ? 'Deselect All' : 'Select All'}
                          </Button>
                        </div>

                        {studentLists.map((list) => (
                          <div key={list.id} className="flex items-start space-x-3">
                            <Checkbox
                              id={list.id}
                              checked={selectedLists.includes(list.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedLists(prev => [...prev, list.id]);
                                } else {
                                  setSelectedLists(prev => prev.filter(id => id !== list.id));
                                }
                              }}
                            />
                            <div className="flex-1">
                              <Label htmlFor={list.id} className="text-sm font-medium cursor-pointer">
                                {list.list_name}
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                {list.instructor_student_emails.length} students
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {studentLists.length > 0 && (
                      <div className="flex justify-end space-x-2 pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowInviteDialog(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSendInvitations}
                          disabled={selectedLists.length === 0}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send Invitations
                        </Button>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Test Details</CardTitle>
                <CardDescription>
                  Update the basic information about your test
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Test Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter test title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe what this test covers..."
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Grade Level</Label>
                    <Input value={`Grade ${test.grade}`} disabled />
                  </div>

                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input value={test.subject} disabled />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="topic">Topic *</Label>
                    <Input
                      id="topic"
                      placeholder="Enter topic"
                      value={formData.topic}
                      onChange={(e) => handleInputChange('topic', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeLimit">Time Limit (minutes)</Label>
                    <Select 
                      onValueChange={(value) => handleInputChange('timeLimit', value)} 
                      value={formData.timeLimit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select time limit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                        <SelectItem value="120">2 hours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveTest}
                    disabled={saving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Questions Section */}
            <Card>
              <CardHeader>
                <CardTitle>Question Management</CardTitle>
                <CardDescription>
                  Add and manage questions for your test
                </CardDescription>
              </CardHeader>
              <CardContent>
                <InstructorQuestionManager 
                  testId={testId!} 
                  testData={{
                    subject: test.subject,
                    grade: test.grade,
                    topic: test.topic
                  }}
                  onQuestionsUpdated={loadTest}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Test Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Status:</span>
                    <Badge variant={test.is_published ? "default" : "secondary"}>
                      {test.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Questions:</span>
                    <span className="text-sm font-medium">{test.total_questions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Time Limit:</span>
                    <span className="text-sm font-medium">
                      {test.time_limit ? `${test.time_limit} min` : 'No limit'}
                    </span>
                  </div>
                </div>
                
                <Separator />
                
                <div className="text-xs text-muted-foreground">
                  Created: {new Date(test.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}